import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { replace, push } from 'react-router-redux';
import * as moment from 'moment';
import { sortBy, find } from 'lodash';
import * as initials from 'initials';
import { List } from 'react-virtualized';
import { Draggable, Droppable } from 'react-drag-and-drop';
import {
  IconButton,
  Icon,
  Loader,
  Tag,
  RouteWrapper,
  Section,
} from '../components';
import { setBookingProvisionalVehicle } from '../redux/actions/bookings';
import { setReservationProvisionalVehicle } from '../redux/actions/reservations';
import { updateVehicleTypeFilter } from '../redux/actions/app';
import { getAvailabilities } from '../redux/actions/availabilities';
import { getHolidayRange } from '../redux/actions/holidays';
import bookingStatuses from '../data/bookingStatuses';

interface Props {
  match: any;
  vehicleTypes: any;
  filterVehicleType: any;
  dispatch: Function;
}

interface State {
  availabilities: any;
  date?: moment.Moment;
  start?: moment.Moment;
  end?: moment.Moment;
  offset: number;
  vehicleTypeId?: number;
  isReady: boolean;
  isLoading: boolean;
  days: Array<moment.Moment>;
  hasMore: boolean;
  hoverData: {
    vehicle: any;
    event: any;
    mouseX: number;
    mouseY: number;
  };
  holidays: Array<any>;
}

class Route extends React.Component<Props, State> {
  element: any;

  constructor(props) {
    super(props);
    this.state = {
      availabilities: [],
      offset: 0,
      isReady: false,
      isLoading: true,
      days: [],
      hasMore: true,
      hoverData: {
        vehicle: false,
        event: false,
        mouseX: 0,
        mouseY: 0,
      },
      holidays: [],
      vehicleTypeId: this.props.filterVehicleType
        ? this.props.filterVehicleType
        : undefined,
    };
    this.handleScroll = this.handleScroll.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.renderHeading = this.renderHeading.bind(this);
    this.renderAvailability = this.renderAvailability.bind(this);
    this.renderEvent = this.renderEvent.bind(this);
    this.renderTooltip = this.renderTooltip.bind(this);
  }

  async componentDidMount() {
    console.log(this.props, 'this.props')
    this.handleDateUpdate(moment(this.props.match.params.period));
  }

  componentWillUnmount() {
    if (this.element) {
      this.element.removeEventListener('scroll', this.handleScroll);
    }
  }

  /**
   * Get an event for a day, if any
   */
  getEventForDay(day, events) {
    if (events) {
      for (const event of events) {
        if (
          moment(event.start)
            .startOf('day')
            .isSameOrBefore(day) &&
          moment(event.end)
            .startOf('day')
            .isSameOrAfter(day)
        ) {
          return event;
        }
      }
    }
    return false;
  }

  /**
   * Listen for when the availabilities is scrolled to the end (UNUSED)
   */
  handleScroll() {
    if (!this.state.isLoading && this.state.hasMore && this.element) {
      if (
        this.element.scrollTop + this.element.offsetHeight >=
        this.element.scrollHeight - 100
      ) {
        this.handleOffsetUpdate(this.state.offset + 15);
      }
    }
  }

  /**
   * Handle a date update for availabilities
   */
  async handleDateUpdate(date: moment.Moment) {
    this.props.dispatch(
      replace(`/availabilities/${date.format('YYYY-MM-DD')}`),
    );
    const start = date
      .clone()
      .subtract(1, 'week')
      .startOf('isoWeek');
    const end = date
      .clone()
      .add(2, 'weeks')
      .endOf('isoWeek');

    let holidays = [];
    const { data, error } = await this.props.dispatch(
      getHolidayRange(start, end),
    );
    if (!error) {
      holidays = data;
    }
    this.setState(
      { date, start, end, holidays, offset: 0, hasMore: true },
      () => {
        this.handleUpdate();
      },
    );
  }

  /**
   * Handle a vehicle type ID update for availabilities
   */
  handleVehicleTypeUpdate(id: number) {
    this.setState(
      { vehicleTypeId: isNaN(id) ? undefined : id, offset: 0, hasMore: true },
      () => {
        this.handleUpdate();
      },
    );
  }

  /**
   * Handle an offset update for availabilities
   */
  handleOffsetUpdate(offset: number) {
    this.setState({ offset, isLoading: true }, () => {
      this.handleUpdate(true);
    });
  }

  /**
   * Fetch new availabilities
   * @param {Boolean} shouldAppend Whether to append the newly loaded events
   * @param {Number | false} forceLoad Whether to forcibly load a set amount of events
   */
  handleUpdate(
    shouldAppend: boolean = false,
    forceLoad: number | false = false,
  ) {
    this.setState({ isLoading: true }, async () => {
      const days = [];
      const day = this.state.start.clone();
      while (day.isSameOrBefore(this.state.end)) {
        days.push(day.clone());
        day.add(1, 'day');
      }

      const response = await this.props.dispatch(
        getAvailabilities({
          limit: forceLoad ? forceLoad : 15,
          start: this.state.start.format('YYYY-MM-DD'),
          end: this.state.end.format('YYYY-MM-DD'),
          vehicle_type_id: this.state.vehicleTypeId,
          offset: forceLoad ? 0 : this.state.offset,
        }),
      );
      const availabilities = response.error ? [] : response.data;

      this.setState({
        days,
        availabilities: shouldAppend
          ? this.state.availabilities.concat(availabilities)
          : availabilities,
        isReady: true,
        isLoading: false,
        hasMore: availabilities.length >= 15,
        hoverData: {
          vehicle: false,
          event: false,
          mouseX: 0,
          mouseY: 0,
        },
      });
    });
  }

  /**
   * Render the list of days
   */
  renderHeading() {
    return (
      <div className="availabilities__row availabilities__row__heading">
        <div className="availabilities__row__item">&nbsp;</div>
        {this.state.days.map((d) => (
          <div
            className={`availabilities__row__item ${
              d.isSame(moment().startOf('day')) ? 'is-highlighted' : ''
            }`}
            key={d.format('YYYY-MM-DD')}
            onClick={() =>
              this.props.dispatch(
                push(`/bookings/status/PENDING/${d.format('YYYY-MM-DD')}`),
              )
            }
          >
            <div>
              <strong>{d.format('ddd')}</strong>
              <br />
              {d.format('Do')}
              <br />
              <small>{d.format('MMM')}</small>
            </div>
          </div>
        ))}
      </div>
    );
  }

  /**
   * Render an individual availability for a vehicle
   */
  renderAvailability({ vehicle, events }, style, isVisible) {
    const type: any = find(this.props.vehicleTypes, {
      id: vehicle.vehicle_type.id,
    });
    return (
      <div className="availabilities__row" key={vehicle.id} style={style}>
        <div className="availabilities__row__item">
          {isVisible && (
            <span className="avatar image is-32x32">
              <img src={type.image} alt={vehicle.vehicle_type.name} />
            </span>
          )}
          {vehicle.vehicle_type.name}
        </div>
        {this.state.days.map((day) => {
          return this.renderEvent(
            vehicle,
            this.getEventForDay(day, events),
            day,
          );
        })}
      </div>
    );
  }

  /**
   * Render an individual event for an availability
   */
  renderEvent(vehicle, event, day: moment.Moment) {
    let content = <Icon icon="plus" size="large" />;
    const isBooking = event.contact_name !== undefined;
    const classNames = ['availabilities__row__item'];

    if (!event) {
      classNames.push('is-free');
      if (day.isSame(moment().startOf('day'))) {
        classNames.push('is-today');
      }
      for (const holiday of this.state.holidays) {
        if (
          moment(holiday.start).isSameOrBefore(day) &&
          moment(holiday.end).isSameOrAfter(day)
        ) {
          classNames.push('is-holiday');
          break;
        }
      }
    } else {
      classNames.push('is-not-free');
      if (isBooking) {
        const bookingStatus = find(bookingStatuses, { value: event.status });
        classNames.push(`is-${bookingStatus.class}`);
        content = initials(event.contact_name);
      } else {
        classNames.push('is-dark');
        const renderIcon = (event) => {
          switch (event.type) {
            case 'MAINTENANCE':
              return 'cogs';
            case 'BROKEN':
              return 'chain-broken';
          }
          return 'lock';
        };
        content = <Icon icon={renderIcon(event)} />;
      }
      if (day.isSame(moment(event.start).startOf('day'))) {
        classNames.push('is-start');
      }
      if (day.isSame(moment(event.end).startOf('day'))) {
        classNames.push('is-end');
      }
    }

    // If the booking is reserved so it as locked
    if (event.deleted_at) {
      content = <Icon icon={'lock'} />;
    }

    const props = {
      className: classNames.join(' '),
      key: day.format('YYYY-MM-DD'),
      onMouseEnter: ({ clientX, clientY }) => {
        if (this.state.hoverData.event !== event) {
          this.setState({
            hoverData: { vehicle, event, mouseX: clientX, mouseY: clientY },
          });
        }
      },
      onClick: () => {
        if (event.deleted_at) {
          return;
        }
        if (event) {
          this.props.dispatch(
            push(`/${isBooking ? 'bookings' : 'reservations'}/${event.id}`),
          );
        } else {
          this.props.dispatch(
            push(
              `/bookings/create?start_date=${day.format(
                'YYYY-MM-DD',
              )}&end_date=${day.format('YYYY-MM-DD')}&vehicle_type_id=${
                vehicle.vehicle_type.id
              }`,
            ),
          );
        }
      },
    };

    if (event) {
      return (
        <Draggable type="event" data={JSON.stringify(event)} {...props}>
          {content}
        </Draggable>
      );
    }

    return (
      <Droppable
        types={['event']}
        onDrop={async (data) => {
          const event = JSON.parse(data.event);
          const isBooking = event.contact_name !== undefined;

          console.log(
            `Dragging ${isBooking ? 'booking' : 'reservation'} #${event.id}...`,
          );

          if (event.vehicle_type_id !== vehicle.vehicle_type.id) {
            console.log(
              `Invalid vehicle type "${vehicle.vehicle_type.id}", expecting "${event.vehicle_type_id}"`,
            );
            return;
          }

          if (event.provisional_vehicle.id === vehicle.id) {
            console.log('Did not detect movement to another vehicle');
            return;
          }

          let response;
          if (isBooking) {
            response = await this.props.dispatch(
              setBookingProvisionalVehicle(event.id, vehicle.id),
            );
          } else {
            response = await this.props.dispatch(
              setReservationProvisionalVehicle(event.id, vehicle.id),
            );
          }

          if (response.error) {
            window.alert(response.error);
            console.log(`Failed to move to vehicle #${vehicle.id}`);
            return;
          }

          console.log(`Successfully moved to vehicle #${vehicle.id}`);
          this.handleUpdate(false, 15 + this.state.offset);
        }}
        {...props}
      >
        {content}
      </Droppable>
    );
  }

  /**
   * Render the appropriate tooltip data
   */
  renderTooltip() {
    const { event, vehicle } = this.state.hoverData;
    if (!event) {
      return null;
    }

    const duration = 1 + moment(event.end).diff(moment(event.start), 'd');
    const isBooking = event.contact_name !== undefined;

    let tagClass = 'dark';
    if (isBooking) {
      const bookingStatus = find(bookingStatuses, { value: event.status });
      tagClass = bookingStatus.class;
    }

    const start = moment(event.start).startOf('day');
    const end = moment(event.end).startOf('day');

    return (
      <div>
        <div className="tags has-addons is-pulled-right">
          {event.vehicle && (
            <Tag type="default">{event.vehicle.registration}</Tag>
          )}
          <Tag type={tagClass}>{isBooking ? event.status : event.type}</Tag>
        </div>
        <strong>
          {isBooking ? event.contact_name :
            ( event.vehicle ? event.vehicle.registration : 'Vehicle no longer exists - please choose another vehicle' )
          }
        </strong>
        <br />
        {start.format('Do MMM')}
        {!start.isSame(end) && ` - ${end.format('Do MMM')}`}
        <br />
        <small>
          {vehicle.vehicle_type.name} {isBooking ? 'booking' : 'reservation'}{' '}
          for {duration} day{duration !== 1 ? 's' : ''}
        </small>
      </div>
    );
  }

  render() {
    if (this.refs.grid && this.element === undefined) {
      this.element = ReactDOM.findDOMNode(this.refs.grid);
      this.element.addEventListener('scroll', this.handleScroll);
    }

    return (
      <RouteWrapper
        title="Availabilities"
        requirePermission="MANAGE_BOOKINGS"
        requireAuth
      >
        <section className="section">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <div className="field is-grouped">
                  <IconButton
                    icon="chevron-left"
                    className={`control is-default${
                      this.state.isLoading ? ' is-loading' : ''
                    }`}
                    handleClick={() => {
                      this.handleDateUpdate(
                        this.state.date.subtract(1, 'week'),
                      );
                    }}
                  >
                    Previous Week
                  </IconButton>
                  <IconButton
                    icon="chevron-right"
                    className={`control is-default${
                      this.state.isLoading ? ' is-loading' : ''
                    }`}
                    isRight
                    handleClick={() => {
                      this.handleDateUpdate(this.state.date.add(1, 'week'));
                    }}
                  >
                    Next Week
                  </IconButton>
                </div>
              </div>
            </div>
            <div className="level-right">
              {this.state.isLoading ? (
                <>
                  <div className="level-item">
                    <Loader />
                  </div>
                  <div className="level-item">
                    <small className="has-text-grey">
                      Loading vehicles, please wait...
                    </small>
                  </div>
                </>
              ) : !this.state.hasMore ? (
                <div className="level-item">
                  <small className="has-text-success">
                    All vehicles have been loaded
                  </small>
                </div>
              ) : (
                <div className="level-item">
                  <small className="has-text-danger">
                    Scroll down to load more vehicles
                  </small>
                </div>
              )}
              <div className="level-item">
                <div
                  className={`select${
                    this.state.isLoading ? ' is-loading' : ''
                  }`}
                >
                  <select
                    value={this.props.filterVehicleType}
                    onChange={(event) => {
                      this.props.dispatch(
                        updateVehicleTypeFilter(event.target.value),
                      );
                      this.handleVehicleTypeUpdate(Number(event.target.value));
                    }}
                  >
                    <option value={null}>All Vehicles</option>
                    {sortBy(this.props.vehicleTypes, ['size', 'id']).map(
                      (vehicleType) => (
                        <option key={vehicleType.id} value={vehicleType.id}>
                          {vehicleType.name}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>
        {this.state.hoverData.event !== false && (
          <div
            key={this.state.hoverData.event.id}
            className="availability-tooltip"
            style={{
              top:
                this.state.hoverData.mouseY >= screen.height - 200
                  ? this.state.hoverData.mouseY - 100
                  : this.state.hoverData.mouseY + 15,
              left:
                this.state.hoverData.mouseX >= screen.width - 400
                  ? this.state.hoverData.mouseX - 400
                  : this.state.hoverData.mouseX + 15,
            }}
          >
            {this.renderTooltip()}
          </div>
        )}
        <div className="availabilities">
          {!this.state.isReady ? (
            <Section>
              <Loader fullwidth />
            </Section>
          ) : (
            this.renderHeading()
          )}
          <List
            ref="grid"
            width={window.innerWidth}
            height={window.innerHeight - 52 - 84 - 82}
            rowCount={this.state.availabilities.length}
            rowHeight={82}
            rowRenderer={({ index, style, isVisible }) =>
              this.renderAvailability(
                this.state.availabilities[index],
                style,
                isVisible,
              )
            }
          />
        </div>
      </RouteWrapper>
    );
  }
}

const mapStateToProps = ({ app, vehicleTypes }) => ({
  filterVehicleType: app.filterVehicleType,
  vehicleTypes: vehicleTypes.vehicleTypes,
});

export default connect(mapStateToProps)(Route);
